import React from "react";
import { Box, Typography } from "@mui/material";

import { getCommentAndColor } from "components/PerformanceReview/helpers";
import { getScoreBand } from "./helpers";

/**
 * Circular score card (mobile) — the Speech / Content ring.
 *
 * Collapsed: title + "Tap to know more" + ring + band chip (all centred).
 * Tapping "Tap to know more" expands a feedback card in beside the ring (both
 * stay visible). In the expanded state the ring card's title moves to the top-
 * left and its CTA/chip drop away, and the feedback card shows the band chip
 * (top-left) + the band-specific feedback text — desktop's
 * `getCommentAndColor(score).speech` / `.content`.
 *
 * @param {string} title
 * @param {number} value      0–100
 * @param {string} category   "speech" | "content" — picks the feedback text
 * @param {string} [subtitle] the reveal CTA (default "Tap to know more")
 */

// Fixed height (not minHeight) so the row is the SAME height collapsed and
// expanded — otherwise it grows to fit the feedback text mid-transition and the
// card visibly jumps. 280 comfortably fits the collapsed ring + chip and the
// expanded feedback text (which scrolls only in the extreme case on tiny phones).
const CARD_HEIGHT = 280;

const CARD_SX = {
  bgcolor: "shades.clr-white-900",
  border: "1px solid",
  borderColor: "neutral.clr-100",
  borderRadius: 3,
  p: 4,
  height: CARD_HEIGHT,
};

function BandChip({ band }) {
  // "Needs Improvement" is a solid red pill with white text in the design; the
  // other bands are light-tinted pills with coloured text.
  const solid = band.label === "Needs Improvement";
  const bgcolor = solid ? "#E2483C" : band.chipBg;
  const color = solid ? "#FFFFFF" : band.chipColor;

  return (
    <Box sx={{ px: 3, py: 1, borderRadius: 1.5, bgcolor, whiteSpace: "nowrap" }}>
      <Typography variant="body02-semiBold" sx={{ color }}>
        {band.label}
      </Typography>
    </Box>
  );
}

export default function ScoreRing({
  title,
  value = 0,
  category = "speech",
  subtitle = "Tap to know more",
}) {
  const [open, setOpen] = React.useState(false);
  const band = getScoreBand(value);
  const feedback = getCommentAndColor(value)[category] || "";

  const toggle = () => setOpen((o) => !o);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: open
          ? "minmax(0, 1fr) minmax(0, 1fr)"
          : "minmax(0, 1fr) minmax(0, 0fr)",
        columnGap: open ? "12px" : "0px",
        alignItems: "stretch",
        transition:
          "grid-template-columns 0.45s cubic-bezier(0.4, 0, 0.2, 1), column-gap 0.45s ease",
      }}
    >
      <RingCard
        title={title}
        value={value}
        subtitle={subtitle}
        band={band}
        open={open}
        onToggle={toggle}
      />

      {/* Feedback card — clipped to width 0 when collapsed, expands in on open. */}
      <Box sx={{ overflow: "hidden", minWidth: 0, display: "flex" }}>
        <FeedbackCard band={band} feedback={feedback} open={open} onClose={toggle} />
      </Box>
    </Box>
  );
}

function RingCard({ title, value, subtitle, band, open, onToggle }) {
  const rounded = Math.round(Number.isFinite(value) ? value : 0);

  const size = 96;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  // Fill the arc from empty to the score on mount (desktop parity).
  const [progress, setProgress] = React.useState(0);
  React.useEffect(() => {
    const id = requestAnimationFrame(() => setProgress(rounded));
    return () => cancelAnimationFrame(id);
  }, [rounded]);

  const dashoffset =
    circumference * (1 - Math.min(Math.max(progress, 0), 100) / 100);

  return (
    <Box
      onClick={onToggle}
      sx={{
        ...CARD_SX,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: open ? "flex-start" : "center",
        gap: 3,
      }}
    >
      {/* Header — centred when collapsed, top-left when expanded */}
      <Box sx={{ textAlign: open ? "left" : "center" }}>
        <Typography variant="h6-semiBold" color="neutral.clr-800">
          {title}
        </Typography>
        {!open && (
          <Typography
            variant="overline-medium"
            color="primary.clr-300"
            sx={{ display: "block", textDecoration: "underline", mt: 0.5 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Ring (+ chip below it when collapsed), centred */}
      <Box
        sx={{
          flex: open ? 1 : "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
        }}
      >
        <Box sx={{ position: "relative", width: size, height: size }}>
          <svg width={size} height={size}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#E9EDF2"
              strokeWidth={stroke}
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={band.ring}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashoffset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              style={{ transition: "stroke-dashoffset 1s ease-out" }}
            />
          </svg>

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h5-bold" color="neutral.clr-900" sx={{ lineHeight: 1 }}>
              {rounded}
            </Typography>
            <Typography variant="body02-regular" color="neutral.clr-500">
              /100
            </Typography>
          </Box>
        </Box>

        {!open && <BandChip band={band} />}
      </Box>
    </Box>
  );
}

function FeedbackCard({ band, feedback, open, onClose }) {
  return (
    <Box
      onClick={onClose}
      sx={{
        ...CARD_SX,
        cursor: "pointer",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        opacity: open ? 1 : 0,
        transition: "opacity 0.4s ease",
      }}
    >
      {/* Chip top-left */}
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <BandChip band={band} />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body02-regular" color="neutral.clr-700" sx={{ textAlign: "center" }}>
          {feedback}
        </Typography>
      </Box>
    </Box>
  );
}
